// Copied from invenire-v2 for its media JSON page: summarizeTracsMedia (src/lib/media-import/tracs-summary.ts)
// and locateParseError (projects/[id]/media/[mediaId]/json/json-source.utils.ts).

export type TracsSessionSummary = {
  label: string;
  /** Unix seconds, as the import stores it; null when the file has none. */
  backupDate: number | null;
  copyType: string | null;
  tracsVersion: string | null;
  fileCount: number;
  totalSize: number;
};

export type TracsMediaSummary = {
  mediaLabel: string;
  mediaType: string | null;
  mediaInformation: string | null;
  sessions: TracsSessionSummary[];
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const stringOrNull = (value: unknown) => (typeof value === 'string' ? value : null);

/**
 * Summarizes a parsed TRACS export (`{ Media: TRACS.Media }`) the way the JSON loader reads it:
 * every FileInfo entry counts and sizes are summed with parseInt. Returns null when the value
 * isn't one.
 */
export function summarizeTracsMedia(value: unknown): TracsMediaSummary | null {
  const media = isRecord(value) ? value.Media : undefined;
  if (
    !isRecord(media) ||
    typeof media.MediaLabel !== 'string' ||
    !Array.isArray(media.BackUpSession)
  ) {
    return null;
  }

  const mediaType = isRecord(media.MediaType) ? media.MediaType : {};

  return {
    mediaLabel: media.MediaLabel,
    mediaType: stringOrNull(mediaType.MediaTypeName),
    mediaInformation: stringOrNull(mediaType.MediaInformation),
    sessions: media.BackUpSession.filter(isRecord).map((session) => {
      const config = isRecord(session.RestorationConfig) ? session.RestorationConfig : {};
      const files = Array.isArray(session.FileInfo) ? session.FileInfo : [];
      const backupDate = Number.parseInt(String(session.BackupDate), 10);

      return {
        label: typeof session.BackUpSessionLabel === 'string' ? session.BackUpSessionLabel : '',
        backupDate: Number.isFinite(backupDate) ? backupDate : null,
        copyType: stringOrNull(config['Copy Type']),
        tracsVersion: stringOrNull(config.TracsVersion),
        fileCount: files.length,
        totalSize: files.reduce<number>((total, file) => {
          const size = isRecord(file) ? Number.parseInt(String(file.Size), 10) : Number.NaN;
          return Number.isFinite(size) ? total + size : total;
        }, 0),
      };
    }),
  };
}

/**
 * Best-effort line/column extraction from a `JSON.parse` error message
 * (V8: "...at position 123 (line 4 column 8)"; older: "...at position 123").
 */
export function locateParseError(text: string, message: string): { line: number; column: number } {
  const lineColMatch = /line\s+(\d+)\s+column\s+(\d+)/i.exec(message);
  if (lineColMatch) {
    return { line: Number(lineColMatch[1]), column: Number(lineColMatch[2]) };
  }
  const posMatch = /position\s+(\d+)/i.exec(message);
  if (posMatch) {
    return positionAt(text, Math.min(Number(posMatch[1]), text.length));
  }
  return { line: 1, column: 1 };
}

/** The 1-based line and column of an offset in the text */
export function positionAt(text: string, offset: number): { line: number; column: number } {
  let line = 1;
  let lineStart = 0;
  for (let i = text.indexOf('\n'); i !== -1 && i < offset; i = text.indexOf('\n', i + 1)) {
    line++;
    lineStart = i + 1;
  }
  return { line, column: offset - lineStart + 1 };
}

/**
 * Where each backup session object starts in the text, in the order summarizeTracsMedia lists
 * them (entries of BackUpSession that aren't objects are skipped, as it skips them).
 */
export function sessionOffsets(text: string): number[] {
  // shortcut: takes the first "BackUpSession" key, which a JSON media export only has under Media
  const array = /"BackUpSession"\s*:\s*\[/.exec(text);
  const offsets: number[] = [];
  let depth = 0;
  for (let i = array ? array.index + array[0].length : text.length; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      for (i++; i < text.length && text[i] !== '"'; i++) {
        if (text[i] === '\\') { i++; }
      }
    } else if (char === '{' || char === '[') {
      if (depth === 0 && char === '{') { offsets.push(i); }
      depth++;
    } else if (char === '}' || char === ']') {
      if (depth === 0) { break; }
      depth--;
    }
  }
  return offsets;
}

/** A diagnostic as the editor reports it (Monaco's IMarker; severity 8 error, 4 warning) */
export type EditorMarker = { severity: number; message: string; startLineNumber: number; startColumn: number };

export type JsonProblem = { error: boolean; message: string; line: number; column: number };

/** The parse error first, then the editor's errors and warnings in file order (its hints and info are left out) */
export function jsonProblems(
  parseError: string | null,
  at: { line: number; column: number } | null,
  markers: EditorMarker[],
): JsonProblem[] {
  const editor = markers
    .filter((m) => m.severity >= 4)
    .sort((a, b) => a.startLineNumber - b.startLineNumber || a.startColumn - b.startColumn)
    .map((m) => ({ error: m.severity >= 8, message: m.message, line: m.startLineNumber, column: m.startColumn }));
  return parseError ? [{ error: true, message: parseError, ...(at ?? { line: 1, column: 1 }) }, ...editor] : editor;
}
